import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { DuoMeasurementValues } from "@novorender/api";
import { Panel, spacing } from "@procore/core-react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "app/redux-store-interactions";
import { type EmptyStateConfig, Window } from "components/window";
import { type BaseWindowProps } from "contexts/windows";
import { MeasuredObject, MeasuredResult } from "features/measure/measuredObjectProcore";
import { measureActions } from "features/measure/measureSlice";
import { type ExtendedMeasureEntity } from "types/misc";

import { useDimensionData } from "./useDimensionData";

export function DimensionWindow({ onClose, position = "center", ...props }: BaseWindowProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { data, loading, error, hasData } = useDimensionData();

    const getEmptyState = (): EmptyStateConfig | undefined => {
        if (hasData) {
            return;
        }
        return {
            title: t("noDimension"),
            message: t("selectSurfaceToViewDimension"),
        };
    };

    const handlePrevious = () => {
        if (data && data.currentIndex > 0) {
            dispatch(measureActions.selectMeasureSet(data.currentIndex - 1));
        }
    };

    const handleNext = () => {
        if (data && data.currentIndex < data.totalSets - 1) {
            dispatch(measureActions.selectMeasureSet(data.currentIndex + 1));
        }
    };

    return (
        <Window
            position={position}
            size={{ width: 400, height: 500 }}
            minSize={{ width: 200, height: 300 }}
            header={
                <Panel.Header onClose={onClose}>
                    <Panel.Title>
                        <Box display="flex" alignItems="center" gap={1}>
                            {t("dimension")}
                            {data && data.totalSets > 1 && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <IconButton
                                        size="small"
                                        onClick={handlePrevious}
                                        disabled={data.currentIndex === 0}
                                        sx={{ padding: 0.5 }}
                                    >
                                        <ChevronLeft fontSize="small" />
                                    </IconButton>
                                    <Typography variant="caption" sx={{ minWidth: "60px", textAlign: "center" }}>
                                        {data.currentIndex + 1} / {data.totalSets}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={handleNext}
                                        disabled={data.currentIndex === data.totalSets - 1}
                                        sx={{ padding: 0.5 }}
                                    >
                                        <ChevronRight fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Panel.Title>
                </Panel.Header>
            }
            loading={loading}
            loadingMessage={t("loadingDimension")}
            error={error ? `${t("failedToLoadDimension")}: ${error}` : null}
            emptyState={getEmptyState()}
            onClose={onClose}
            secondaryBgColor={true}
            data-testid="dimension-window"
            {...props}
        >
            <DimensionContent data={data} />
        </Window>
    );
}

interface DimensionData {
    entities: ExtendedMeasureEntity[];
    result: DuoMeasurementValues | undefined;
    currentIndex: number;
    totalSets: number;
}

interface DimensionContentProps {
    data: DimensionData | null;
}

function DimensionContent({ data }: DimensionContentProps) {
    if (!data || (data.entities.length === 0 && !data.result)) {
        return null;
    }

    return (
        <div
            style={{
                flex: 1,
                padding: spacing.lg,
                display: "flex",
                flexDirection: "column",
                gap: spacing.sm,
                overflowY: "auto",
            }}
        >
            {data.entities.map((obj, idx) => (
                <MeasuredObject obj={obj as ExtendedMeasureEntity} idx={idx} key={idx} />
            ))}
            {data.result && <MeasuredResult duoMeasurementValues={data.result} />}
        </div>
    );
}
